import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getSubscriptionPlans, createPaymentIntent, subscribeToPlans } from '../../../Services/User/SubscriptionPlans';
import { showToast } from '../../../Components/Toast/Toast';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import StripePayment from '../Payment/StripePayment';
import './SubscriptionPlans.css';
import CustomButton from '../../../Components/CustomButton';

const SubscriptionPlans = () => {
  usePageTitle('Subscription Plans');
  const navigate = useNavigate();
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const { data: response, isLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: getSubscriptionPlans,
  });

  // Categorize plans by base name (Basic, Standard, Premium)
  const categorizePlans = (plans) => {
    const categories = {};

    plans.forEach(plan => {
      // Extract category name (e.g., "Basic" from "Basic Monthly")
      const categoryMatch = plan.name.match(/^(\w+)\s+(Monthly|Yearly)$/i);
      if (categoryMatch) {
        const categoryName = categoryMatch[1];
        const interval = categoryMatch[2].toLowerCase();

        if (!categories[categoryName]) {
          categories[categoryName] = {
            name: categoryName,
            monthly: null,
            yearly: null
          };
        }

        if (interval === 'monthly') {
          categories[categoryName].monthly = plan;
        } else if (interval === 'yearly') {
          categories[categoryName].yearly = plan;
        }
      }
    });

    return Object.values(categories);
  };

  const handleChoosePlan = async (plan) => {
    try {
      setIsCreatingIntent(true);
      setSelectedPlan(plan);

      // Create payment intent
      const response = await createPaymentIntent(parseFloat(plan.price));
      
      if (response?.client_secret) {
        setClientSecret(response.client_secret);
        setShowPayment(true);
      } else {
        showToast('Failed to initialize payment. Please try again.', 'error');
        setIsCreatingIntent(false);
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      showToast(error?.message || 'Failed to initialize payment', 'error');
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Verify payment intent status
      if (paymentIntent.status !== 'succeeded') {
        showToast('Payment not completed. Please try again.', 'error');
        return;
      }

      // Add a small delay to ensure Stripe has fully processed the charge
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Call subscribe endpoint
      const response = await subscribeToPlans(selectedPlan.slug, paymentIntent.id);
      
      if (response?.status) {
        showToast('Subscription successful!', 'success');
        // Navigate to subscription logs or success page
        setTimeout(() => {
          navigate('/subscription-logs');
        }, 500);
      } else {
        showToast(response?.message || 'Subscription failed. Please contact support.', 'error');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      const errorMessage = error?.message || error?.error || 'Subscription failed. Please contact support.';
      showToast(errorMessage, 'error');
    } finally {
      setShowPayment(false);
      setSelectedPlan(null);
      setClientSecret(null);
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    showToast(error?.message || 'Payment failed', 'error');
    setIsCreatingIntent(false);
  };

  const handleBackFromPayment = () => {
    setShowPayment(false);
    setSelectedPlan(null);
    setClientSecret(null);
    setIsCreatingIntent(false);
  };

  const renderPlanCard = (plan, interval) => {
    if (!plan) return null;

    const isYearly = interval === 'yearly';
    const monthlySavings = isYearly ? (plan.price / 12).toFixed(2) : null;

    return (
      <Col md={6} className="mb-3">
        <div className="plan-card justify-content-between">
          <div>
            <div className="plan-header">
              <div className="plan-radio">
                <input
                  type="radio"
                  id={`plan-${plan.id}`}
                  name={`plan-group-${plan.name.split(' ')[0]}`}
                  className="plan-radio-input"
                />
                <label htmlFor={`plan-${plan.id}`} className="plan-radio-label">
                  {isYearly ? 'Yearly' : 'Monthly'}
                </label>
              </div>
            </div>
            <p className="plan-description">
              Lorem ipsum dolor sit amet consectetur adipiscing elit.
            </p>
            <div className="text-start">
              <div className="plan-price">${plan.price}</div>
              {isYearly && monthlySavings && (
                <div className="plan-savings">
                  Get ${monthlySavings} on 10 coins 🔥
                </div>
              )}
            </div>
          </div>
          <div className="text-start">
            <CustomButton
              variant="primary"
              className=""
              onClick={() => handleChoosePlan(plan)}
              disabled={isCreatingIntent}
              loading={isCreatingIntent && selectedPlan?.id === plan.id}
            >
              {isCreatingIntent && selectedPlan?.id === plan.id ? 'Processing...' : 'Choose Plan'}
            </CustomButton>
          </div>
        </div>
      </Col>
    );
  };

  const renderPlanCategory = (category) => {
    return (
      <div key={category.name} className="plan-category mb-5">
        <h3 className="plan-category-title">{category.name} Subscription Plan</h3>
        <p className="plan-category-description">
          Lorem Ipsum Dolor Sit Amet, Consectetur Adipiscing Elit, Nonumy Eirmod Blanditon Laoeret. Pran Gravida
          Dolor Sit Amet Loeu Accumsan Et Viverra Justo Commodo Pran Sed;duo Pulvinar Tempor Cum Socio Nacional
          Penatibus Et Magnis Dis Parturient!
        </p>
        <div className="plan-cards-wrapper">
          <Row>
            {renderPlanCard(category.yearly, 'yearly')}
            {renderPlanCard(category.monthly, 'monthly')}
          </Row>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <>
        <InnerBanner heading="Subscription Plans" />
        <section className="py-sm-5 py-4">
          <Container>
            <Row>
              <Col xs={12} className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading subscription plans...</p>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }

  if (!response?.data || response.data.length === 0) {
    return (
      <>
        <InnerBanner heading="Subscription Plans" />
        <section className="py-sm-5 py-4">
          <Container>
            <Row>
              <Col xs={12} className="text-center">
                <p className="mt-3">No subscription plans available</p>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }

  const categorizedPlans = categorizePlans(response.data);

  // Show payment screen if payment is initiated
  if (showPayment && selectedPlan && clientSecret) {
    return (
      <StripePayment
        onBack={handleBackFromPayment}
        amount={parseFloat(selectedPlan.price)}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        clientSecret={clientSecret}
      />
    );
  }

  return (
    <>
      <InnerBanner heading="Subscription Plans" />
      <section className="py-sm-5 py-4">
        <Container>
          <Row>
            <Col xs={12} className="mb-4 text-center">
              <Button
                variant="primary"
                className="subscription-logs-btn"
                onClick={() => navigate('/subscription-logs')}
              >
                View Subscription Logs
              </Button>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <div className="subscription-plans-container">
                {categorizedPlans.map(category => renderPlanCategory(category))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default SubscriptionPlans;
